import React from 'react';
import { CTASection } from '../../components/CTASection';
import { Home, CheckCircle, Sparkles, Shield, Zap, Sun } from 'lucide-react';
export function YardSigns() {
  const benefits = [{
    icon: <Sparkles className="w-6 h-6" data-id="element-2046" />,
    title: 'Vibrant Designs',
    description: 'High-quality materials and vibrant colors that catch the eye and brighten any landscape'
  }, {
    icon: <Shield className="w-6 h-6" data-id="element-2047" />,
    title: 'Weather-Resistant',
    description: 'Built to withstand the elements—tough and beautiful through rain or shine'
  }, {
    icon: <Zap className="w-6 h-6" data-id="element-2048" />,
    title: 'Easy Installation',
    description: 'Lightweight and simple to set up—display your message in no time'
  }, {
    icon: <Sun className="w-6 h-6" data-id="element-2049" />,
    title: 'Custom Everything',
    description: 'Fully customizable for any occasion, event, or business need'
  }];
  const uses = [{
    icon: '🎂',
    title: 'Birthday Celebrations',
    description: 'Playful birthday greetings that make the day extra special',
    examples: ['Milestone birthdays', 'Surprise parties', 'Birthday countdowns']
  }, {
    icon: '🎓',
    title: 'Graduations',
    description: 'Celebrate academic achievements with pride',
    examples: ['High school grads', 'College grads', 'Kindergarten grads']
  }, {
    icon: '💍',
    title: 'Weddings & Engagements',
    description: 'Announce your special day or engagement with style',
    examples: ['Save the date', 'Wedding directions', 'Just married']
  }, {
    icon: '🏠',
    title: 'Real Estate',
    description: 'Professional signs for open houses and property sales',
    examples: ['Open house', 'For sale', 'Coming soon']
  }, {
    icon: '🎉',
    title: 'Special Events',
    description: 'Promote events and gatherings in your community',
    examples: ['Garage sales', 'Fundraisers', 'Community events']
  }, {
    icon: '🏢',
    title: 'Business Promotion',
    description: 'Drive traffic and promote your business',
    examples: ['Grand openings', 'Sales events', 'Directional signs']
  }, {
    icon: '🎄',
    title: 'Seasonal Decor',
    description: 'Celebrate holidays with themed signs',
    examples: ['Christmas', 'Halloween', 'Easter']
  }, {
    icon: '🗳️',
    title: 'Political Campaigns',
    description: 'Show support for candidates and causes',
    examples: ['Campaign signs', 'Vote reminders', 'Issue advocacy']
  }];
  const features = ['Full-color printing', 'Custom sizes available', 'Durable corrugated plastic', 'UV-resistant inks', 'Double-sided options', 'Wire stakes included', 'Quick turnaround', 'Affordable pricing'];
  return <div className="flex flex-col w-full" data-id="element-2050">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-wh-teal to-teal-600 py-20" data-id="element-2051">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2052">
          <div className="flex justify-center mb-6" data-id="element-2053">
            <Home className="w-16 h-16 text-white" data-id="element-2054" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-2055">
            Custom Yard Signs
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto" data-id="element-2056">
            Make a statement, one sign at a time! Your yard is your
            canvas—announce, celebrate, or advertise with flair using custom
            yard signs that shine.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white" data-id="element-2057">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2058">
          <div className="max-w-4xl mx-auto mb-16" data-id="element-2059">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-2060">
              Your Yard, Your Canvas!
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4" data-id="element-2061">
              <p data-id="element-2062">
                At Wear Haus Designs, we believe your yard is more than just a
                patch of grass—it's an opportunity to express yourself! Our
                custom yard signs let you announce, celebrate, or advertise with
                flair. Whether it's a birthday bash, a graduation celebration,
                or promoting your business, we've got the perfect sign to make
                your message shine!
              </p>
              <p data-id="element-2063">
                From playful birthday greetings to professional marketing
                messages, our yard signs can be customized for any occasion.
                Made with high-quality materials and vibrant colors, they're
                built to withstand the elements while catching every eye that
                passes by.
              </p>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-id="element-2064">
            {benefits.map((benefit, index) => <div key={index} className="bg-gray-50 rounded-xl p-8 border border-gray-100" data-id="element-2065">
                <div className="flex items-start gap-4" data-id="element-2066">
                  <div className="flex-shrink-0 w-12 h-12 bg-wh-pink rounded-lg flex items-center justify-center text-white" data-id="element-2067">
                    {benefit.icon}
                  </div>
                  <div data-id="element-2068">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-2069">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600" data-id="element-2070">{benefit.description}</p>
                  </div>
                </div>
              </div>)}
          </div>

          {/* Features */}
          <div className="bg-gradient-to-br from-wh-teal to-teal-600 rounded-2xl p-12 text-white mb-16" data-id="element-2071">
            <h2 className="text-3xl font-bold mb-8 text-center" data-id="element-2072">
              Why Choose Our Yard Signs?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto" data-id="element-2073">
              {features.map((feature, index) => <div key={index} className="flex items-center gap-3" data-id="element-2074">
                  <CheckCircle className="w-6 h-6 text-wh-yellow flex-shrink-0" data-id="element-2075" />
                  <span className="text-lg" data-id="element-2076">{feature}</span>
                </div>)}
            </div>
          </div>

          {/* Use Cases */}
          <div className="mb-16" data-id="element-2077">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-2078">
              Endless Possibilities
            </h2>
            <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto" data-id="element-2079">
              From event announcements to business promotion and seasonal decor,
              our yard signs are perfect for any occasion. Here are some popular
              uses:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-2080">
              {uses.map((use, index) => <div key={index} className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-wh-teal transition-colors" data-id="element-2081">
                  <div className="text-5xl mb-4 text-center" data-id="element-2082">{use.icon}</div>
                  <h3 className="text-xl font-bold text-wh-teal mb-2 text-center" data-id="element-2083">
                    {use.title}
                  </h3>
                  <p className="text-sm text-gray-700 mb-3 text-center" data-id="element-2084">
                    {use.description}
                  </p>
                  <ul className="space-y-1" data-id="element-2085">
                    {use.examples.map((example, exIndex) => <li key={exIndex} className="text-xs text-gray-600 flex items-center gap-2" data-id="element-2086">
                        <span className="w-1 h-1 bg-wh-pink rounded-full" data-id="element-2087"></span>
                        {example}
                      </li>)}
                  </ul>
                </div>)}
            </div>
          </div>

          {/* Process */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-2088">
            <div className="bg-white rounded-xl p-8 border-2 border-wh-pink text-center" data-id="element-2089">
              <div className="w-16 h-16 bg-wh-pink rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4" data-id="element-2090">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2091">
                Choose Your Design
              </h3>
              <p className="text-gray-700" data-id="element-2092">
                Work with our creative team or provide your own artwork
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border-2 border-wh-teal text-center" data-id="element-2093">
              <div className="w-16 h-16 bg-wh-teal rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4" data-id="element-2094">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2095">
                We Print & Prepare
              </h3>
              <p className="text-gray-700" data-id="element-2096">
                High-quality printing on durable, weather-resistant materials
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border-2 border-wh-yellow text-center" data-id="element-2097">
              <div className="w-16 h-16 bg-wh-yellow rounded-full flex items-center justify-center text-gray-900 text-2xl font-bold mx-auto mb-4" data-id="element-2098">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2099">
                Display with Pride
              </h3>
              <p className="text-gray-700" data-id="element-2100">
                Easy setup with included stakes—your message is ready to shine
              </p>
            </div>
          </div>

          {/* Installation Highlight */}
          <div className="bg-gradient-to-br from-wh-yellow to-yellow-500 rounded-2xl p-12 mb-16" data-id="element-2101">
            <div className="max-w-3xl mx-auto text-center" data-id="element-2102">
              <Zap className="w-16 h-16 mx-auto mb-6 text-gray-900" data-id="element-2103" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-2104">
                Easy Installation
              </h2>
              <p className="text-xl text-gray-800 mb-6" data-id="element-2105">
                Lightweight and easy to set up, you can have your message
                displayed in no time! Each sign comes with sturdy wire stakes
                that make installation a breeze. Simply push the stakes into the
                ground, and your sign is ready to make an impact.
              </p>
              <p className="text-lg text-gray-700" data-id="element-2106">
                No tools required, no complicated assembly—just instant curb
                appeal!
              </p>
            </div>
          </div>

          {/* CTA Box */}
          <div className="bg-gradient-to-br from-wh-pink to-pink-600 rounded-2xl p-12 text-center text-white" data-id="element-2107">
            <Home className="w-16 h-16 mx-auto mb-6" data-id="element-2108" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2109">
              Let's Get Started!
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-2110">
              Ready to create your custom yard sign? Our creative team is here
              to help you design a sign that captures your spirit and gets
              noticed! Contact us today and let your yard speak volumes!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2111">
              <a href="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-white text-wh-pink font-bold rounded-lg hover:bg-gray-100 transition-colors" data-id="element-2112">
                Get a Quote
              </a>
              <a href="/services" className="inline-flex items-center justify-center px-8 py-4 bg-wh-yellow text-gray-900 font-bold rounded-lg hover:bg-yellow-400 transition-colors" data-id="element-2113">
                View All Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection data-id="element-2114" />
    </div>;
}